import moment from 'moment-timezone';
import { createSelector } from 'reselect';
import getOr from 'lodash/fp/getOr';
import omit from 'lodash/fp/omit';
import pipe from 'lodash/fp/pipe';
import sumBy from 'lodash/fp/sumBy';
import { toPairs, mapValues } from 'lodash';
import {
  all,
  map,
  find,
  prop,
  pluck,
  curry,
  chain,
  propEq,
  filter,
  sortBy,
  reduce,
  values,
  evolve,
  reverse,
  groupBy,
  contains,
} from 'ramda';
import { makeDetails, calculatePayoutSummary } from './payoutReportUtils';
import { getById } from '../barbers/selector';
import { mapServiceData, mapProductData, sumReports } from './payouts/helpers';
import sumServicesReport from './barbersReport/sumReport';
import sumProductsReport from './productSalesReport/sumReport';
import { activeShop, getActiveBrandId, isBrandViewActive } from '../../auth/selectors';
import config from '../../../config';
import { GME_BRAND_ID } from './constants';

export const getBarberReports = (state) => state.reports.barberReports;

export const isFilterActive = (id) => pipe(find(propEq('id', id)), getOr(false, 'checked'));

export const getPaymentFilters = (state) => state.reports.paymentFilters;
export const getStatusFilters = (state) => state.reports.statusFilters;
export const getAppointmentBookedByFilters = (state) => state.reports.appointmentBookedByFilters;
export const getAppointmentStatusFilters = (state) => state.reports.appointmentStatusFilters;
export const getAppointmentPlatformFilters = (state) => state.reports.appointmentPlatformFilters;
export const getBrandFilters = (state) => state.reports.brandFilters;
export const getServiceFilters = (state) => state.reports.serviceFilters;
export const getReportFiltersDirty = (state) => state.reports.filtersDirty;
export const getProductInventoryActionFilters = (state) => state.reports.productInventoryActionFilters;
export const getProductCategoryFilters = (state) => state.reports.productCategoryFilters;

export const getAppointmentsReport = (state) => state.reports.appointments;

export const getSelectedFilterNames = pipe(filter(prop('checked')), map(prop('title')));
export const getSelectedFilterSlugs = pipe(filter(prop('checked')), map(prop('slug')));

export const getShopFilters = (state) => state.reports.shopFilters;

export const extractFilterIds = reduce((acc, item) => {
  if (item.checked) {
    return [...acc, item.id];
  }
  return acc;
}, []);

export const getProductCategoryFiltersAllChecked = createSelector(
  getProductCategoryFilters,
  all((item) => item.checked)
);

export const getActiveProductCategoryIds = createSelector(getProductCategoryFilters, extractFilterIds);

export const getActiveBrandIds = createSelector(getBrandFilters, extractFilterIds);

export const getActiveServiceIds = createSelector(getServiceFilters, extractFilterIds);

const getProductSalesData = (state) => state.reports.productSalesReport;

export const getPayoutReportNoCommissionSplits = (state) => state.reports.payoutReportsNoCommissionSplit;
export const getPayoutPagination = (state) => state.reports.payoutPagination;

export const getBarbersFromFilter = (state) => state.reports.barberFilter.map((barberId) => getById(state, barberId));

export const getAllBarbersFilter = (state) => state.reports.barberFilter;
export const getSalesBarbersFilter = (state) => state.reports.saleReport.barberFilter;

export const getActiveBarbersFilterIds = createSelector(getSalesBarbersFilter, extractFilterIds);

const areAllBarbersSelected = createSelector(
  getSalesBarbersFilter,
  getActiveBarbersFilterIds,
  (allBarbers, selectedBarbers) => allBarbers.length === selectedBarbers.length
);

export const getSelectedBarberFilterIds = createSelector(
  areAllBarbersSelected,
  getActiveBarbersFilterIds,
  (isAllSelected, activeIds) => (isAllSelected ? null : activeIds)
);

export const getSalesActiveBarbersFilterIds = createSelector(getSalesBarbersFilter, extractFilterIds);

export const getTotalAvailableForPayout = getOr(0, 'reports.payoutsReports.shop.totalAvailableForPayout');
export const getGiftCardTipsTransfered = getOr(0, 'reports.payoutsReports.shop.giftCardTipsAmount');
export const getPayoutReportServiceItems = (state) => mapServiceData(getOr([], 'reports.payoutsReports.items', state));
export const getPayoutReportProductItems = (state) => mapProductData(getOr([], 'reports.payoutsReports.items', state));
export const getPayoutReportFromDate = getOr('', 'reports.payoutsReports.fromDate');
export const getPayoutReportToDate = getOr('', 'reports.payoutsReports.toDate');
export const getPayoutReportLastPayout = getOr('', 'reports.payoutsReportsLastPayout');

const omitExtraFields = omit(['id', 'shopId', 'transferId', 'fromDate', 'toDate', 'createdAt', 'updatedAt']);

export const getPayoutReportHistory = (state) =>
  mapValues(state.reports.payoutHistory.details, (items) => map(pipe(omitExtraFields), items));

export const getPayoutReportRawData = (state) => state.reports.payoutHistory.rawData;

export const getPayoutReportPagination = (state) => state.reports.payoutHistory.pagination;

export const getPayoutReportHistoryFromRaw = (state) => {
  const rawData = getPayoutReportRawData(state);
  const details = makeDetails(rawData);
  return mapValues(details, (items) => map(pipe(omitExtraFields), items));
};

export const getPayoutReportSummaryFromRaw = (state) => {
  const rawData = getPayoutReportRawData(state);
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return [];
  }
  const details = makeDetails(rawData);
  return Object.values(details).map(calculatePayoutSummary);
};

// TODO: make all filters consistent. Now both array of barber ids and array of barbers possible
const ensureConsistency = map((item) => {
  if (typeof item === 'string') {
    return {
      id: item,
      checked: true,
    };
  }

  return item;
});

const getCheckedBarberIds = pipe(
  ensureConsistency,
  filter((barber) => barber.checked),
  pluck('id')
);
const filterByBarberId = curry((barbers, report) =>
  contains(report.barberId || 'unassigned', getCheckedBarberIds(barbers))
);
export const filterByBarber = (barbers, reports) => filter(filterByBarberId(barbers), reports);

export const getProductSalesReport = createSelector(
  getSalesActiveBarbersFilterIds,
  getProductSalesData,
  filterByBarber
);

export const getServiceSalesReport = createSelector(getSalesActiveBarbersFilterIds, getBarberReports, filterByBarber);

export const getServiceSalesSum = createSelector(getServiceSalesReport, sumServicesReport);

export const getServicesCount = createSelector(getServiceSalesSum, prop('totalCount'));

export const getProductSalesSum = createSelector(getProductSalesReport, sumProductsReport);

export const getProductsCount = createSelector(getProductSalesSum, prop('totalCount'));

export const getDiscounts = (state) => state.reports.discountAmount;

export const getDiscountAmount = createSelector(getActiveBarbersFilterIds, getDiscounts, filterByBarber);

export const getTotalSum = createSelector(getServiceSalesSum, getProductSalesSum, (services, products) => {
  const response = sumReports([...toPairs(services), ...toPairs(products)]);
  return response;
});

export const getTotalDiscounts = createSelector(
  getTotalSum,
  (totals) => (totals.discountAmount || 0) + (totals.promo || 0)
);

export const getCanceledAppointmentsCount = createSelector(getTotalSum, prop('canceledAppointmentCount'));

export const getCanceledAppointmentsAmount = createSelector(getTotalSum, prop('canceledAmount'));

export const getNoShowAppointmentsCount = createSelector(getTotalSum, prop('noShowAppointmentCount'));

export const getNoShowAppointmentsAmount = createSelector(getTotalSum, prop('noShowAmount'));

export const shopServicesCount = (state) => state.reports.shopServicesCount;

export const filterShopServicesCount = createSelector(getActiveBarbersFilterIds, shopServicesCount, filterByBarber);
const orderById = sortBy(prop('id'));
const groupById = groupBy((item) => item.id);
const sumQuantity = pipe(
  values,
  map(
    reduce(
      (acc, item) => ({
        ...item,
        quantity: item.quantity + acc.quantity,
      }),
      {
        quantity: 0,
      }
    )
  )
);
const unnestServices = chain(prop('services'));
const processServices = pipe(unnestServices, orderById, groupById, sumQuantity);

export const getServicesCountTable = createSelector(filterShopServicesCount, (reports) => {
  const services = processServices(reports);
  const sorted = pipe(sortBy(prop('quantity')), reverse)(services);
  const totalSold = reduce((acc, item) => acc + item.quantity, 0, sorted);
  return map((item) => ({
    Service: item.name,
    '#': item.quantity,
    '%': Math.round((item.quantity / totalSold) * 100),
  }))(sorted);
});
const sortByDate = sortBy((item) => item.arrival_date);
const sortByDateReverse = pipe(sortByDate, reverse);

const getLastDeposit = (state) => {
  const sortedReports = sortByDateReverse(state.reports.depositsReport);
  return find((item) => item.status === 'paid', sortedReports);
};

const getNextDeposit = (state) => {
  const sortedReports = sortByDate(state.reports.depositsReport);
  return find((item) => item.status === 'in_transit', sortedReports);
};

export const getDepositsForDashboard = (state) => ({
  last: getLastDeposit(state),
  next: getNextDeposit(state),
});

const toDate = (timestamp) => new Date(timestamp * 1000);
const updateArrivalDate = (deposits) =>
  deposits && {
    ...deposits,
    arrivalDate: toDate(deposits.arrival_date),
  };
export const getDashboardDeposits = createSelector(
  getDepositsForDashboard,
  evolve({
    last: updateArrivalDate,
    next: updateArrivalDate,
  })
);

export const filterRefunds = createSelector(
  getSalesActiveBarbersFilterIds,
  getOr([], 'reports.discountAmount'),
  filterByBarber
);

export const getTotalRefunds = createSelector(filterRefunds, sumBy('totalRefund'));

export const getServiceAndAppointmentRefunds = createSelector(
  filterRefunds,
  sumBy((refund) => refund.totalAppointmentRefund + refund.totalServiceRefund)
);

export const getProductRefunds = createSelector(filterRefunds, sumBy('totalProductRefund'));

export const getServiceCategoryFilters = (state) => state.reports.serviceCategoryFilter;
export const getServiceCategoryFiltersAllChecked = createSelector(
  getServiceCategoryFilters,
  all((item) => item.checked)
);

export const getActiveCategoryIds = createSelector(getServiceCategoryFilters, extractFilterIds);

export const getServiceCategoryFilterForApi = createSelector(
  getServiceCategoryFiltersAllChecked,
  getActiveCategoryIds,
  (isAllSelected, activeIds) => (isAllSelected ? [] : activeIds)
);

export const getBrandFiltersAllChecked = createSelector(
  getBrandFilters,
  all((item) => item.checked)
);

export const getBrandFiltersForApi = createSelector(
  getBrandFiltersAllChecked,
  getActiveBrandIds,
  (isAllSelected, activeIds) => (isAllSelected ? [] : activeIds)
);

export const getIsAutoDeposit = createSelector(activeShop, getOr(false, 'commissionSplitsAutoDeposit'));

export const getDateRange = ({ startDate, endDate, timezone }) => {
  const start = moment.tz(startDate, config.dateFormat, timezone).startOf('day');
  const end = moment.tz(endDate, config.dateFormat, timezone).endOf('day');

  return { start, end };
};

export const canLoadReport = ({ reports: { startDate, endDate }, auth: { timezone } }) => {
  const { start, end } = getDateRange({ startDate, endDate, timezone });

  return end.diff(start, 'days') <= config.exportReportDays;
};

export const isGmeBrandView = (state) => isBrandViewActive(state) && getActiveBrandId(state) === GME_BRAND_ID;
